import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { SelectFieldComponent, TextButtonComponent, TextFieldComponent } from '@custom-components';
import { TranslateModule } from '@ngx-translate/core';
import { switchMap } from 'rxjs';
import { AZIMUTH_PRESETS, EXACT, TILT_PRESETS } from '../pv-forecast-presets';
import {
  PvForecastEmeterChannel,
  PvForecastFormGroup,
  PvArrayGroup,
  createPvArrayGroup,
} from './pv-forecast-form';

// Renders a form the host owns, so the host decides when it is saved and what a save means.
@Component({
  selector: 'xemex-pv-forecast-form',
  imports: [
    NgTemplateOutlet,
    ReactiveFormsModule,
    TranslateModule,
    MatSelectModule,
    SelectFieldComponent,
    TextFieldComponent,
    TextButtonComponent,
  ],
  templateUrl: './pv-forecast-form.component.html',
  styleUrl: './pv-forecast-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PvForecastFormComponent {
  readonly form = input.required<PvForecastFormGroup>();
  readonly emeterChannels = input.required<PvForecastEmeterChannel[]>();

  protected readonly tiltPresets = TILT_PRESETS;
  protected readonly azimuthPresets = AZIMUTH_PRESETS;
  protected readonly exact = EXACT;

  private readonly changeDetector = inject(ChangeDetectorRef);

  constructor() {
    // A host changes the controls without an event in this view, when it marks them touched.
    toObservable(this.form)
      .pipe(
        switchMap(form => form.events),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.changeDetector.markForCheck());
  }

  protected get pvArrays(): PvArrayGroup[] {
    return this.form().controls.pvArrays.controls;
  }

  protected addArray(): void {
    this.form().controls.pvArrays.push(createPvArrayGroup());
  }

  protected removeArray(index: number): void {
    const arrays = this.form().controls.pvArrays;
    if (arrays.length > 1) {
      arrays.removeAt(index);
    }
  }

  protected isInvalid(control: AbstractControl): boolean {
    return control.invalid && control.touched;
  }
}
